import {get as _get} from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';
import {Button, Header, Icon} from 'semantic-ui-react';

import { CIDR, NOT_SAVED } from '@constants/messages';
import {parseProducts} from '@modules/client/utils/whitelistingHelper';
import {showPopupMessage} from '@utils/popupMessage/PopupMessageService';

import {convertToInt} from './helpers';

export const errorMessage = `
    An error occurred while trying to save the CIDR. It might be that the CIDR is overlapping another CIDR
`;

const CIDR_EXTERNAL_LINK_PROPS = {
    rel: 'noopener noreferrer',
    target: '_blank',
};

const CIDR_WIKIPEDIA_URL = 'https://en.wikipedia.org/wiki/Classless_Inter-Domain_Routing';
const CIDR_IPV4_TOOL_URL = 'https://www.ipaddressguide.com/cidr';
const CIDR_IPV6_TOOL_URL = 'https://www.ipaddressguide.com/ipv6-cidr';

const CidrMoreInfoMessage = () => (
    <>
        Find out more about CIDR
        <a href={CIDR_WIKIPEDIA_URL} {...CIDR_EXTERNAL_LINK_PROPS}>
            &nbsp;here
        </a>.
    </>
);

const CidrLowerBoundaryMessage = () => (
    <>
        Make sure that the IP included in the CIDR is equal to the lower boundary of the CIDR block
        (first IP of the range). To do that, use
        <a href={CIDR_IPV4_TOOL_URL} {...CIDR_EXTERNAL_LINK_PROPS}>
            &nbsp;this tool for IPv4
        </a>
        &nbsp;and
        <a href={CIDR_IPV6_TOOL_URL} {...CIDR_EXTERNAL_LINK_PROPS}>
            &nbsp;this tool for IPv6
        </a>.
    </>
);

export const CidrFormInfoMessage = () => (
    <div className='--block --paddingBottom-14'>
        <p>
            <CidrMoreInfoMessage />
        </p>
        <p>
            <CidrLowerBoundaryMessage />
        </p>
    </div>
);

export const CidrRangesInfoTooltip = () => (
    <>
        <p>
            Enter IPv4 or IPv6 CIDR ranges (e.g., 80.66.42.208/28 or 2a06:56c0:12:121:b86c:c50f:8838:5e89/128).
            Multiple entries can be separated by commas or line breaks.
        </p>
        <p>
            <CidrMoreInfoMessage />
        </p>
        <p>
            <CidrLowerBoundaryMessage />
        </p>
    </>
);

export const overlapConfirmationModal = (description, overlappingIps, confirmationText, onClickYes, onClickNo, isLoading) => {
    return (
        <div>
            <Header content='CIDR overlap' />
            <div className='content'>
                <div>
                    {description}
                    <ul>
                        {overlappingIps}
                    </ul>
                    <p>
                        Whitelisted IPs have priority over blacklisted IPs, which means that an IP which is both
                        whitelisted and blacklisted will be whitelisted.
                    </p>
                    <p>{confirmationText}</p>
                </div>
            </div>
            <div className='actions'>
                <Button
                    icon
                    labelPosition='right'
                    color='green'
                    onClick={onClickYes}
                    disabled={isLoading}
                    loading={isLoading}
                >
                    Yes
                    <Icon name={'checkmark'} key={0}/>
                </Button>
                <Button
                    icon
                    labelPosition='right'
                    color='red'
                    onClick={onClickNo}
                    disabled={isLoading}
                >
                    No
                    <Icon name={'remove'} key={1}/>
                </Button>
            </div>
        </div>
    );
};
overlapConfirmationModal.propTypes = {
    description: PropTypes.string.isRequired,
    overlappingIps: PropTypes.object.isRequired,
    confirmationText: PropTypes.string.isRequired,
    onClickYes: PropTypes.func.isRequired,
    onClickNo: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
};

export const cidrFormTabs = (content) => {
    return [
        {
            id: 'step1',
            key: 'step1',
            menuItem: {content: 'Add CIDR', hidden: true},
            pane: {
                content: content,
            },
        },
        {
            id: 'step2',
            key: 'step2',
            menuItem: {content: 'Confirmation', hidden: true},
            pane: {
                content: null,
            },
        },
    ];
};
cidrFormTabs.propTypes = {
    content: PropTypes.func.isRequired,
};

export const prepareStringForCidrSorting = (row) => (
    `${Boolean(row.is_disabled)}-${convertToInt(row.ip_version.id)}-${row.cidr}`
);

export const cidrColumns = {
    'ip_version': {
        label: 'IP version',
        sorting: row => _get(row, 'ip_version.name', null),
        header: {
            align: 'center',
            isCollapsed: true,
        },
        content: {align: 'center'},
    },
    'cidr': {label: CIDR, sorting: true},
    'products': {
        label: 'Products',
        sorting: row => parseProducts(row['products']),
    },
    'description': {label: 'Description', sorting: true},
    'creation_datetime': {
        content: {align: 'center'},
        header: {align: 'center'},
        label: 'Added',
        sorting: true,
    },
    'disable_datetime': {
        content: {align: 'center'},
        header: {align: 'center'},
        label: 'Disabled',
        sorting: true,
    },
    'actions': {
        label: 'Actions',
        header: {
            align: 'center',
            isCollapsed: true,
        },
        content: {align: 'center'},
    },
};

export const showSuccessMessageAfterMutation = (cidr) => {
    showPopupMessage(`The CIDR "${cidr}" has been saved successfully.`, 'success');
};

export const showErrorMessage = (cidr, isTypeWhiteList = true) => {
    showPopupMessage(
        NOT_SAVED(CIDR, cidr),
        'error',
        true,
        isTypeWhiteList ? `${errorMessage.trim()} of the client.` : `${errorMessage.trim()}.`
    );
};
